<template>
  <div
    v-show="visible"
    class="history"
    :class="{ active: true }"
    @wheel="preventScroll"
    v-click-outside="handleClickOutside"
  >
    <div class="tabs">
      <div class="tab-wrapper">
        <nav class="tabs is-boxed" role="tablist" aria-labelledby="history-title">
          <a
            v-for="category in categories"
            :key="category"
            class="tab"
            :class="{ 'is-active': category === selected_category }"
            role="tab"
            @click="selected_category = category"
          >
            {{ category }}
          </a>
        </nav>
      </div>
    </div>
    <ul role="list" class="content" ref="list">
      <li v-for="entry in filteredEntries" :key="entry.url">
        <div class="item-wrapper">
          <a :href="entry.url" class="item" @click.stop.prevent="onLinkActivated(entry)">
            <font-awesome-icon class="icon" :icon="['fal', entry.icon ? entry.icon : 'bookmark']" fixed-width />
            <div class="item-content">
              <div class="item-title">{{ entry.name }}</div>
              <div class="item-subtitle">
                <div>{{ entry.category }}</div>
                <small :title="entry.timestamp">{{ timeSince(entry.timestamp) }}</small>
              </div>
            </div>
          </a>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import Vue from 'vue'
import { DateTime } from '@/util/luxon'

export default Vue.extend({
  name: 'history-table',
  props: {
    visible: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      selected_category: 'Senaste'
    }
  },
  methods: {
    onLinkActivated(entry) {
      this.$emit('close')
      this.$router.push(entry.url)
    },
    preventScroll(event) {
      const list = this.$refs.list
      const isTargetInList = event.target === list || list.contains(event.target)
      const isScrollable = isTargetInList && list.scrollHeight > list.clientHeight

      if (isScrollable) {
        // Allow scrolling within the element, but stop propagation to the parent
        event.stopPropagation()
      } else {
        // Prevent the default scroll behavior if the element isn't scrollable
        event.preventDefault()
      }
    },
    handleClickOutside() {
      if (!this.visible) {
        return
      }
      this.$emit('close')
    },
    timeSince(date) {
      const duration = DateTime.fromProfileDate(date)
      if (!duration.isValid) {
        return ''
      }
      return duration.toRelative()
    }
  },
  computed: {
    entries: {
      get() {
        return this.$store.getters.activeHistory
      },
      deep: true
    },
    filteredEntries() {
      if (this.selected_category === 'Senaste') {
        return this.entries
      }
      return this.entries.filter(entry => entry.category === this.selected_category)
    },
    categories() {
      const categories = new Set(['Senaste'])
      this.entries.forEach(entry => {
        if (entry.category) {
          categories.add(entry.category)
        }
      })
      return Array.from(categories).sort((a, b) => (a === 'Senaste' ? -1 : b === 'Senaste' ? 1 : a.localeCompare(b)))
    }
  }
})
</script>

<style scoped>
.history {
  --padding: 1rem;
  --border-width: 1px;

  max-height: var(--app-viewport-height);
  min-width: 20rem;
  border-left: var(--border-width) solid var(--silver);
  border-bottom: var(--border-width) solid var(--silver);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  position: fixed;
  min-height: 0;
  background: #fff;
  z-index: 12;
  right: 0;
  cursor: pointer;
  &.active {
    cursor: default;
    min-height: var(--app-viewport-height);
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    padding: var(--padding);
    .tabs {
      display: flex;
    }
    .content {
      display: block;
    }
    .title {
      display: block;
    }
    .header {
      .icon {
        margin-right: 0.5rem;
        color: var(--deep-sapphire);
      }
    }
  }
  .title {
    margin-right: 1rem;
    color: black;
    display: none;
    font-size: 1rem;
    font-weight: 400;
  }
  .tabs {
    display: none;
  }
  .content {
    display: none;
  }
}

.header {
  justify-content: space-between;
  display: flex;
  align-items: center;
  background-color: var(--light-grey);
  .logo {
    display: flex;
    align-items: center;
    .icon {
      font-size: 1.5rem;
    }
  }
  .close-button {
    .icon {
      color: var(--grey);
      font-size: 1.25rem;
    }
    &:hover {
      cursor: pointer;
      color: var(--dark-grey);
    }
  }
}

.tabs {
  margin-top: 0.5rem;
  border-bottom: 1px solid var(--silver);
  margin-bottom: 0;
  .tab {
    transition: background-color 0.2s ease, color 0.2s ease;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 6px 6px 0 0;
    &.is-active {
      font-weight: 600;
      border-bottom: 2px solid var(--deep-sapphire);
      color: var(--deep-sapphire);
    }
    &:hover {
      color: var(--deep-sapphire);
      background-color: var(--blue-light); /* Change background color on hover */
    }
  }
}

.content {
  margin-top: 0.5rem;
  overflow-y: auto;
  flex-grow: 1;
  li {
    transition: background-color 0.2s ease, color 0.2s ease;
    border-bottom: 1px solid var(--silver);
    .item-wrapper {
      padding: 0.5rem 0.5rem;
      .item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        text-decoration: none;
        color: inherit;
        .item-content {
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          > * {
            padding-top: 0.25rem;
          }
          .item-title {
            font-weight: 600;
          }
          .item-subtitle {
            display: flex;
            justify-content: space-between;
            font-size: 0.875rem;
            color: var(--grey);
          }
        }
        .icon {
          color: var(--grey);
          margin-left: -0.5rem;
          margin-right: 0.5rem;
          font-size: 1.25rem;
        }
      }
    }

    &:hover {
      background-color: var(--blue-light); /* Change background color on hover */
      color: var(--deep-sapphire); /* Change text color on hover */
    }

    &:hover .icon {
      color: var(--deep-sapphire); /* Change icon color on hover */
    }
  }
}
</style>
